// ProfileUpdateForm.js
// Public user profile update form for job portal


import React, { useState, useEffect, useRef } from 'react';
import { useGlobalData } from '../context/GlobalDataContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './ProfileUpdateForm.css';
import { getEligibilityCriterias } from '../api/api';


const ProfileUpdateForm = () => {
  const navigate = useNavigate();
    // Role check from JWT
    useEffect(() => {
      let userRole = '';
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const payload = JSON.parse(atob(token.split('.')[1]));
          userRole = payload.role || '';
        }
      } catch (e) {}
      if (userRole !== 'user') {
        navigate('/publicuser/login', { replace: true });
      }
    }, [navigate]);
  const { profile, setProfile } = useGlobalData();
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [showNotif, setShowNotif] = useState(false);
  const [showErrorNotif, setShowErrorNotif] = useState(false);
  // Show loading only if profile is completely missing (null/undefined)
  const [loading, setLoading] = useState(profile === null);
  const [eligibilityBanner, setEligibilityBanner] = useState('');

  // Sync with dark mode from Navbar
  useEffect(() => {
    const syncTheme = () => {
      if (document.body.classList.contains('dark-mode')) {
        document.documentElement.setAttribute('data-theme', 'dark');
      } else {
        document.documentElement.setAttribute('data-theme', 'light');
      }
    };
    syncTheme();
    window.addEventListener('darkModeChanged', syncTheme);
    return () => window.removeEventListener('darkModeChanged', syncTheme);
  }, [setProfile]);

  // Fetch user profile and eligibility criteria on mount ONLY if profile is empty, and only once per mount
  const hasFetchedOnce = useRef(false);
  useEffect(() => {
    if (hasFetchedOnce.current) return;
    if (profile !== null) {
      hasFetchedOnce.current = true;
      setLoading(false);
      return;
    }
    const fetchProfileAndCheckEligibility = async () => {
      setLoading(true);
      setError('');
      try {
        const apiUrl = process.env.REACT_APP_API_URL || '/api';
        const token = localStorage.getItem('token');
        if (!token) {
          setError('No login token found. Please login again.');
          setLoading(false);
          hasFetchedOnce.current = true;
          return;
        }
        const res = await axios.get(`${apiUrl}/publicuser/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const user = res.data.user;
        setProfile(prev => {
          const updated = { ...prev, ...user };
          window.dispatchEvent(new Event('dataChanged'));
          return updated;
        });

        // Fetch eligibility criteria and check for match
        const eligRes = await getEligibilityCriterias();
        const criterias = eligRes.data || [];
        // Check for match
        // Education group mapping and ranking
        const educationGroups = {
          school: ['10th', '12th', 'Diploma'],
          ug: ['B.A.', 'B.Sc.', 'B.Com.', 'B.Tech', 'BCA', 'BBA'],
          pg: ['M.A.', 'M.Sc.', 'M.Com.', 'M.Tech', 'MBA', 'MCA'],
          doctorate: ['PhD']
        };
        const groupOrder = ['school', 'ug', 'pg', 'doctorate'];
        // Find user's group
        function getUserGroup(education) {
          for (const group of groupOrder) {
            if (educationGroups[group].includes(education)) return group;
          }
          return null;
        }
        const matched = criterias.find(criteria => {
          // Education group, location, age, and gender ALL must match (all required)
          if (!criteria.education || !criteria.location || !criteria.minAge || !criteria.maxAge) return false;
          if (!user.education || !user.location || !user.age) return false;
          const userGroup = getUserGroup(user.education);
          const requiredGroup = criteria.education;
          const userGroupIndex = groupOrder.indexOf(userGroup);
          const requiredGroupIndex = groupOrder.indexOf(requiredGroup);
          const eduMatch = userGroupIndex !== -1 && requiredGroupIndex !== -1 && userGroupIndex >= requiredGroupIndex;
          // If eligibility location is 'India', allow any user location; else require exact match
          const locMatch = criteria.location === 'India' ? true : criteria.location === user.location;
          const age = Number(user.age);
          const minAge = Number(criteria.minAge);
          const maxAge = Number(criteria.maxAge);
          const ageMatch = age >= minAge && age <= maxAge;
          // Gender match logic
          const genderMatch = !criteria.gender || criteria.gender === 'all' || criteria.gender === user.gender;
          return eduMatch && locMatch && ageMatch && genderMatch;
        });
        if (matched) {
          setEligibilityBanner(`🎉 Congratulations! Your profile matches the eligibility for "${matched.jobTitle}".`);
        } else {
          setEligibilityBanner('');
        }
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to fetch profile');
      } finally {
        setLoading(false);
        hasFetchedOnce.current = true;
      }
    };
    fetchProfileAndCheckEligibility();
  }, [setProfile, profile]);

  const handleChange = (e) => {
    setProfile({ ...profile, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccess('');
    setError('');
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const token = localStorage.getItem('token');
      if (!token) {
        setError('No login token found. Please login again.');
        setShowErrorNotif(true);
        setTimeout(() => setShowErrorNotif(false), 3500);
        return;
      }
      // Remove minAge and maxAge if present before sending
      const { minAge, maxAge, ...profileToSend } = profile;
      const payload = { profile: profileToSend };
      // Always use login time email/mobile from localStorage for update identity
      const loginEmail = localStorage.getItem('userEmail');
      const loginMobile = localStorage.getItem('userMobile');
      if (loginEmail) payload.email = loginEmail;
      if (loginMobile) payload.mobile = loginMobile;
      if (!payload.email && !payload.mobile) {
        setError('Please login again.');
        setShowErrorNotif(true);
        setTimeout(() => setShowErrorNotif(false), 3500);
        return;
      }
      const res = await axios.put(
        `${apiUrl}/publicuser/profile`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(res.data.message || 'Profile updated successfully!');
      setShowNotif(true);
      // Save updated profile to localStorage for NotificationPanel
      window.dispatchEvent(new Event('dataChanged'));
      setTimeout(() => setShowNotif(false), 3500);
    } catch (err) {
      setError(err.response?.data?.error || 'Profile update failed');
      setShowErrorNotif(true);
      setTimeout(() => setShowErrorNotif(false), 3500);
    }
  };

  if (loading && profile === null) {
    return <div className="profile-update-form-container"><div>Loading profile...</div></div>;
  }

  return (
    <div className="profile-update-form-container">
      {/* Toast notifications for profile update */}
      {showNotif && (
        <div className="registration-success-toast">
          {success}
        </div>
      )}
      {showErrorNotif && (
        <div className="registration-error-toast">
          {error}
        </div>
      )}
      {/* Eligibility banner at the very top */}
      {eligibilityBanner && (
        <div style={{
          background: '#e0ffe0',
          color: '#1a4d1a',
          border: '2px solid #2ecc40',
          borderRadius: 8,
          padding: '12px 18px',
          margin: '0 0 16px 0',
          fontWeight: 600,
          fontSize: 18,
          textAlign: 'center',
          boxShadow: '0 2px 8px #0001',
          position: 'relative',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 2
        }}>
          {eligibilityBanner}
        </div>
      )}
      {/* ...existing code for the form... */}
      <form className="profile-update-form" onSubmit={handleSubmit} autoComplete="off">
        <h2>Profile</h2>
        <input name="name" placeholder="Name" value={profile.name} onChange={handleChange} required />
        <input name="email" type="email" placeholder="Email" value={profile.email} onChange={handleChange} required={!!profile.email} />
        <input name="mobile" type="tel" placeholder="Mobile Number" value={profile.mobile} onChange={handleChange} pattern="[0-9]{10}" maxLength={10} minLength={10} required={!!profile.mobile} />

        <div className="form-group">
          <label htmlFor="gender" className="form-label">Gender</label>
          <select
            id="gender"
            name="gender"
            className="styled-select"
            value={profile.gender}
            onChange={handleChange}
            required
          >
            <option value="">Select Gender</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="education" className="form-label">
            <span role="img" aria-label="Education" style={{marginRight: '6px', verticalAlign: 'middle'}}>🎓</span>
            Education
          </label>
          <select
            id="education"
            name="education"
            className="styled-select"
            value={profile.education}
            onChange={handleChange}
            required
          >
            <option value="">Select Education</option>
            <optgroup label="School/Pre-UG">
              <option value="10th">10th</option>
              <option value="12th">12th</option>
              <option value="Diploma">Diploma</option>
            </optgroup>
            <optgroup label="UG (Undergraduate)">
              <option value="B.A.">B.A.</option>
              <option value="B.Sc.">B.Sc.</option>
              <option value="B.Com.">B.Com.</option>
              <option value="B.Tech">B.Tech</option>
              <option value="BCA">BCA</option>
              <option value="BBA">BBA</option>
            </optgroup>
            <optgroup label="PG (Postgraduate)">
              <option value="M.A.">M.A.</option>
              <option value="M.Sc.">M.Sc.</option>
              <option value="M.Com.">M.Com.</option>
              <option value="M.Tech">M.Tech</option>
              <option value="MBA">MBA</option>
              <option value="MCA">MCA</option>
            </optgroup>
            <optgroup label="Doctorate">
              <option value="PhD">PhD</option>
            </optgroup>
            <option value="Other">Other</option>
          </select>
        </div>
        <input name="skills" placeholder="Skills (comma separated, optional)" value={profile.skills} onChange={handleChange} />
        <input name="experience" placeholder="Experience (e.g. 2 years, optional)" value={profile.experience} onChange={handleChange} />
        <div className="form-group">
          <label htmlFor="location" className="form-label">Location (State/UT)</label>
          <select
            id="location"
            name="location"
            className="styled-select"
            value={profile.location}
            onChange={handleChange}
            required
          >
            <option value="">Select State/UT</option>
            <option value="Andhra Pradesh">Andhra Pradesh</option>
            <option value="Arunachal Pradesh">Arunachal Pradesh</option>
            <option value="Assam">Assam</option>
            <option value="Bihar">Bihar</option>
            <option value="Chhattisgarh">Chhattisgarh</option>
            <option value="Goa">Goa</option>
            <option value="Gujarat">Gujarat</option>
            <option value="Haryana">Haryana</option>
            <option value="Himachal Pradesh">Himachal Pradesh</option>
            <option value="Jharkhand">Jharkhand</option>
            <option value="Karnataka">Karnataka</option>
            <option value="Kerala">Kerala</option>
            <option value="Madhya Pradesh">Madhya Pradesh</option>
            <option value="Maharashtra">Maharashtra</option>
            <option value="Manipur">Manipur</option>
            <option value="Meghalaya">Meghalaya</option>
            <option value="Mizoram">Mizoram</option>
            <option value="Nagaland">Nagaland</option>
            <option value="Odisha">Odisha</option>
            <option value="Punjab">Punjab</option>
            <option value="Rajasthan">Rajasthan</option>
            <option value="Sikkim">Sikkim</option>
            <option value="Tamil Nadu">Tamil Nadu</option>
            <option value="Telangana">Telangana</option>
            <option value="Tripura">Tripura</option>
            <option value="Uttar Pradesh">Uttar Pradesh</option>
            <option value="Uttarakhand">Uttarakhand</option>
            <option value="West Bengal">West Bengal</option>
            <option value="Andaman and Nicobar Islands">Andaman and Nicobar Islands</option>
            <option value="Chandigarh">Chandigarh</option>
            <option value="Dadra and Nagar Haveli and Daman and Diu">Dadra and Nagar Haveli and Daman and Diu</option>
            <option value="Delhi">Delhi</option>
            <option value="Jammu and Kashmir">Jammu and Kashmir</option>
            <option value="Ladakh">Ladakh</option>
            <option value="Lakshadweep">Lakshadweep</option>
            <option value="Puducherry">Puducherry</option>
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="age" className="form-label">Age</label>
          <select
            id="age"
            name="age"
            className="styled-select"
            value={profile.age || ''}
            onChange={handleChange}
            required
          >
            <option value="">Select Age</option>
            {Array.from({ length: 45 }, (_, i) => 16 + i).map(age => (
              <option key={age} value={age}>{age}</option>
            ))}
          </select>
        </div>
        <button type="submit">Update</button>
        {error && <div className="error">{error}</div>}
        {success && <div className="success">{success}</div>}
      </form>
    </div>
  );
};

export default ProfileUpdateForm;
